import { SetStateAction, useCallback, useRef, useState } from "react";
import useMemoizedFn from "@/hooks/useMemoizedFn";

type Options<T> = {
	value?: T;
	defaultValue?: T;
	onChange?: (v: T) => void;
};

const useUpdate = () => {
	const [, setState] = useState({});

	return useCallback(() => setState({}), []);
};

export function usePropsValue<T>(options: Options<T>) {
	const { value, defaultValue, onChange } = options;

	const update = useUpdate();

	const stateRef = useRef<T>(value !== undefined ? value : (defaultValue as T));
	if (value !== undefined) {
		stateRef.current = value;
	}

	const setState = useMemoizedFn((v: SetStateAction<T>, forceTrigger: boolean = false) => {
		// `forceTrigger` means trigger `onChange` even if `v` is the same as `stateRef.current`
		const nextValue = typeof v === "function" ? (v as (prevState: T) => T)(stateRef.current) : v;
		if (!forceTrigger && nextValue === stateRef.current) return;
		stateRef.current = nextValue;
		update();
		return onChange?.(nextValue);
	});
	return [stateRef.current, setState] as const;
}
